import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, Users, UserPlus, Clock, CaretLeft, CaretRight,
  ShieldCheck, House, Envelope, Info,
} from "@phosphor-icons/react";

// Panel de Usuarios (Admin) — READ-ONLY.
// Muestra métricas de crecimiento y actividad, con listado paginado.
// El backend valida `role === "admin"`; esta página se protege también con
// <ProtectedRoute allowedRoles={["admin"]}> en App.js.

const ROLE_OPTIONS = [
  { value: "all",   label: "Todos los roles" },
  { value: "admin", label: "Admin" },
  { value: "agent", label: "Asesor" },
];

const SORT_OPTIONS = [
  { value: "created_at_desc",    label: "Registro más reciente" },
  { value: "created_at_asc",     label: "Registro más antiguo" },
  { value: "last_login_desc",    label: "Último acceso" },
  { value: "last_activity_desc", label: "Última actividad" },
  { value: "name_asc",           label: "Nombre A–Z" },
  { value: "email_asc",          label: "Correo A–Z" },
];

const PAGE_SIZE = 25;

const formatDT = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export function AdminUsersPage() {
  const [summary, setSummary] = useState(null);
  const [loadingSum, setLoadingSum] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [role, setRole] = useState("all");
  const [sort, setSort] = useState("created_at_desc");
  const [offset, setOffset] = useState(0);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load summary once (and refresh on demand)
  const loadSummary = async () => {
    setLoadingSum(true);
    try {
      const { data } = await api.get("/admin/users/summary");
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo cargar el resumen"));
    } finally { setLoadingSum(false); }
  };

  // Load list with filters
  const loadList = async () => {
    setLoadingList(true); setError("");
    try {
      const params = { limit: PAGE_SIZE, offset, sort };
      if (debouncedSearch) params.search = debouncedSearch;
      if (role !== "all")  params.role = role;
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(formatApiError(e, "No se pudo cargar el listado"));
    } finally { setLoadingList(false); }
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [debouncedSearch, role, sort, offset]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const measuredSince = summary?.measured_since || "2026-02-19";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="admin-users-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold flex items-center gap-2">
          <ShieldCheck size={14} weight="fill" /> Administración
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1 text-[#C8A24A]">Usuarios</h1>
        <p className="text-sm text-[#C8A24A]/70 mt-2 leading-relaxed">
          Vista de solo lectura. Sin acciones sobre cuentas.
        </p>
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-[#C8A24A]/20 bg-[#151515] p-3">
          <Info size={16} weight="fill" className="text-[#C8A24A] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#C8A24A]/70 leading-relaxed">
            La medición de actividad comienza el <strong>{measuredSince}</strong>. Los usuarios registrados antes de esa fecha mostrarán <em>“Sin actividad registrada”</em> hasta que vuelvan a iniciar sesión.
          </p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="kpi-total" icon={Users} label="Total usuarios"
                 value={loadingSum ? "…" : summary?.total ?? 0} />
        <KpiCard testId="kpi-new-24h" icon={UserPlus} label="Nuevos hoy"
                 value={loadingSum ? "…" : summary?.new?.today ?? 0} />
        <KpiCard testId="kpi-new-7d" icon={UserPlus} label="Nuevos 7 días"
                 value={loadingSum ? "…" : summary?.new?.d7 ?? 0} />
        <KpiCard testId="kpi-new-30d" icon={UserPlus} label="Nuevos 30 días"
                 value={loadingSum ? "…" : summary?.new?.d30 ?? 0} />
        <KpiCard testId="kpi-active-24h" icon={Clock} label="Activos 24 h"
                 value={loadingSum ? "…" : summary?.active?.h24 ?? 0} tint="emerald" />
        <KpiCard testId="kpi-active-7d" icon={Clock} label="Activos 7 días"
                 value={loadingSum ? "…" : summary?.active?.d7 ?? 0} tint="emerald" />
        <KpiCard testId="kpi-active-30d" icon={Clock} label="Activos 30 días"
                 value={loadingSum ? "…" : summary?.active?.d30 ?? 0} tint="emerald" />
        <KpiCard testId="kpi-admins" icon={ShieldCheck} label="Admins"
                 value={loadingSum ? "…" : summary?.by_role?.admin ?? 0} tint="gold" />
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C8A24A]/60" />
          <input
            type="text"
            placeholder="Buscar por nombre o correo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 h-10 rounded-lg bg-[#151515] border border-[#C8A24A]/20 text-[#C8A24A] placeholder:text-[#C8A24A]/40 text-base focus:outline-none focus:border-[#C8A24A]/60"
            data-testid="admin-search-input"
          />
        </div>
        <select
          value={role}
          onChange={(e) => { setRole(e.target.value); setOffset(0); }}
          className="h-10 px-3 rounded-lg bg-[#151515] border border-[#C8A24A]/20 text-[#C8A24A] text-base"
          data-testid="admin-role-filter"
        >
          {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value); setOffset(0); }}
          className="h-10 px-3 rounded-lg bg-[#151515] border border-[#C8A24A]/20 text-[#C8A24A] text-base"
          data-testid="admin-sort-select"
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-2xl border border-[#C8A24A]/20 bg-[#1A1A1A] overflow-hidden" data-testid="admin-users-table">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F] text-[#C8A24A]/70">
              <tr>
                <Th>Usuario</Th>
                <Th>Rol</Th>
                <Th>Registrado</Th>
                <Th>Último acceso</Th>
                <Th>Última actividad</Th>
                <Th className="text-right">Props.</Th>
                <Th className="text-right">Leads</Th>
              </tr>
            </thead>
            <tbody>
              {loadingList && (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-t border-[#C8A24A]/10">
                    <td className="px-4 py-3"><div className="h-4 w-40 bg-[#0F0F0F] rounded animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-16 bg-[#0F0F0F] rounded animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 bg-[#0F0F0F] rounded animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 bg-[#0F0F0F] rounded animate-pulse" /></td>
                    <td className="px-4 py-3"><div className="h-4 w-24 bg-[#0F0F0F] rounded animate-pulse" /></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 w-6 bg-[#0F0F0F] rounded animate-pulse ml-auto" /></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 w-6 bg-[#0F0F0F] rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))
              )}
              {!loadingList && items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#C8A24A]/60 text-sm">Sin resultados</td></tr>
              )}
              {!loadingList && items.map(u => (
                <tr key={u.id} className="border-t border-[#C8A24A]/10 hover:bg-[#C8A24A]/[0.03]" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#C8A24A]">{u.name || "—"}</div>
                    <div className="text-xs text-[#C8A24A]/60 flex items-center gap-1"><Envelope size={12} /> {u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-4 py-3 text-[#C8A24A]/80">{formatDT(u.created_at) || "—"}</td>
                  <td className="px-4 py-3 text-[#C8A24A]/80">
                    {u.last_login_at ? formatDT(u.last_login_at) : <NoActivity />}
                  </td>
                  <td className="px-4 py-3 text-[#C8A24A]/80">
                    {u.last_activity_at ? formatDT(u.last_activity_at) : <NoActivity />}
                  </td>
                  <td className="px-4 py-3 text-right text-[#C8A24A]/90 font-medium">{u.properties_count}</td>
                  <td className="px-4 py-3 text-right text-[#C8A24A]/90 font-medium">{u.inquiries_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden">
          {loadingList && (
            <div className="p-4 text-sm text-[#C8A24A]/60">Cargando…</div>
          )}
          {!loadingList && items.length === 0 && (
            <div className="p-6 text-center text-sm text-[#C8A24A]/60">Sin resultados</div>
          )}
          {!loadingList && items.map(u => (
            <div key={u.id} className="p-4 border-t border-[#C8A24A]/10" data-testid={`admin-user-card-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-[#C8A24A] truncate">{u.name || "—"}</div>
                  <div className="text-sm text-[#C8A24A]/60 truncate">{u.email}</div>
                </div>
                <RoleBadge role={u.role} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <Meta label="Registrado" value={formatDT(u.created_at) || "—"} />
                <Meta label="Últ. acceso" value={u.last_login_at ? formatDT(u.last_login_at) : "Sin actividad registrada"} />
                <Meta label="Últ. actividad" value={u.last_activity_at ? formatDT(u.last_activity_at) : "Sin actividad registrada"} />
                <Meta label="Propiedades / Leads" value={`${u.properties_count} / ${u.inquiries_count}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="border-t border-[#C8A24A]/15 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#C8A24A]/70" data-testid="admin-pagination-info">
            Página <strong>{page}</strong> de <strong>{pageCount}</strong> · {total} usuarios
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
              disabled={!canPrev}
              className="h-9 px-3 rounded-lg border border-[#C8A24A]/30 text-[#C8A24A] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#C8A24A]/10 flex items-center gap-1 text-sm"
              data-testid="admin-prev-page"
            >
              <CaretLeft size={14} /> Anterior
            </button>
            <button
              onClick={() => setOffset(o => o + PAGE_SIZE)}
              disabled={!canNext}
              className="h-9 px-3 rounded-lg border border-[#C8A24A]/30 text-[#C8A24A] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#C8A24A]/10 flex items-center gap-1 text-sm"
              data-testid="admin-next-page"
            >
              Siguiente <CaretRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-3 text-sm text-red-300" data-testid="admin-users-error">{error}</div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-left ${className}`}>{children}</th>
);

const NoActivity = () => (
  <span className="text-xs text-[#C8A24A]/40 italic">Sin actividad registrada</span>
);

const Meta = ({ label, value }) => (
  <>
    <span className="text-xs uppercase tracking-wider text-[#C8A24A]/50 font-semibold">{label}</span>
    <span className="text-sm text-[#C8A24A]/80 truncate">{value}</span>
  </>
);

const RoleBadge = ({ role }) => {
  const isAdmin = role === "admin";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${
      isAdmin
        ? "bg-[#C8A24A]/15 text-[#C8A24A] border-[#C8A24A]/40"
        : "bg-white/5 text-[#C8A24A]/70 border-[#C8A24A]/20"
    }`} data-testid={`admin-role-badge-${role}`}>
      {isAdmin ? <ShieldCheck size={11} weight="fill" /> : <House size={11} weight="fill" />}
      {isAdmin ? "Admin" : "Asesor"}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, testId, tint = "gold" }) => {
  const tintMap = {
    gold:    "text-[#C8A24A] border-[#C8A24A]/25",
    emerald: "text-emerald-300 border-emerald-500/25",
  };
  const t = tintMap[tint] || tintMap.gold;
  return (
    <div className={`p-4 rounded-2xl bg-[#151515] border ${t}`} data-testid={testId}>
      <Icon size={18} weight="duotone" />
      <p className="text-xs uppercase tracking-wider mt-2 text-[#C8A24A]/60 font-semibold">{label}</p>
      <p className="text-2xl font-heading font-bold mt-0.5 text-[#C8A24A]">{value}</p>
    </div>
  );
};

export default AdminUsersPage;
